import { useHistory } from 'react-router-dom';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import ReactSelect from 'react-select';

import { useGetRegionals } from 'hooks/regionals';
import { useCreateUnit } from 'hooks/units';

import {
	Button,
	Flex,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Grid,
	HStack,
	Input,
	Text,
	Select,
	useColorModeValue,
	useToast,
	Skeleton
} from '@chakra-ui/react';

import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';

import { msLightTheme, msDarkTheme } from 'variables/multiselectStyle';
import states from 'variables/states';

const schema = yup.object().shape({
	name: yup.string().required('O campo Nome é obrigatório'),
	regional_id: yup.string().required('O campo Regional é obrigatório')
});

const CreateUnit = () => {
	const { data, loading: loadingRegionals } = useGetRegionals();
	const regionals = data?.listRegionals || [];
	const methods = useForm({
		mode: 'onBlur',
		reValidateMode: 'onBlur',
		resolver: yupResolver(schema)
	});
	const toast = useToast();
	const history = useHistory();

	const {
		control,
		register,
		handleSubmit,
		formState: { errors },
		reset,
		setValue
	} = methods;

	const handleStatesChange = (value) => {
		setValue('states', value);
	};

	const [createUnit, { loading }] = useCreateUnit({
		onSuccess: (data) => {
			reset();
			toast({
				title: 'Unidade cadastrada com sucesso.',
				status: 'success',
				duration: 5000,
				isClosable: true
			});
			history.push(
				`/sistema/administracao/unidades/${data.createUnit.id}`
			);
		},
		onError: () => {
			toast({
				title: 'Ocorreu um erro',
				status: 'error',
				duration: 5000,
				isClosable: true
			});
		}
	});

	const onSubmit = (data) => {
		const regionalId = data.regional_id;
		delete data.regional_id;
		delete data.states;
		const variables = {
			input: {
				...data,
				regional: {
					connect: regionalId
				}
			}
		};
		createUnit({ variables });
	};

	const inputHover = useColorModeValue('health.800', 'health.800');
	const textColor = useColorModeValue('gray.700', 'white');

	return (
		<Flex
			direction="column"
			minH="100vh"
			align="center"
			pt={{ sm: '120px', lg: '160px' }}
		>
			<Card w={{ md: '100%', lg: '50%' }}>
				<CardHeader mb="40px">
					<Flex direction="column">
						<Text
							color={textColor}
							fontSize="lg"
							fontWeight="bold"
							mb="3px"
						>
							Cadastrar Unidade
						</Text>
						<Text
							color="gray.400"
							fontWeight="normal"
							fontSize="sm"
						>
							Preencha os campos para realizar o cadastro
						</Text>
					</Flex>
				</CardHeader>
				<CardBody>
					<FormProvider {...methods}>
						<form
							style={{ width: '100%' }}
							onSubmit={handleSubmit(onSubmit)}
						>
							<Flex direction="column" w="100%">
								<Grid
									templateColumns={{
										sm: '1fr',
										md: 'repeat(2, 1fr)'
									}}
									gap="24px"
								>
									<FormControl isInvalid={errors.regional_id}>
										<FormLabel
											color={textColor}
											fontWeight="bold"
											fontSize="xs"
										>
											Regional
										</FormLabel>
										<Skeleton
											height="40px"
											isLoaded={!loadingRegionals}
										>
											<Select
												placeholder="Selecione uma opção..."
												{...register('regional_id')}
											>
												{regionals.map((reg) => (
													<option value={reg.id}>
														{reg.name}
													</option>
												))}
											</Select>
										</Skeleton>
										{errors.regional_id && (
											<FormErrorMessage>
												{errors.regional_id.message}
											</FormErrorMessage>
										)}
									</FormControl>
									<FormControl isInvalid={errors.name}>
										<FormLabel
											color={textColor}
											fontWeight="bold"
											fontSize="xs"
										>
											Nome
										</FormLabel>
										<Input
											{...register('name')}
											focusBorderColor={inputHover}
											borderRadius="8px"
											fontSize="md"
										/>
										{errors.name && (
											<FormErrorMessage>
												{errors.name.message}
											</FormErrorMessage>
										)}
									</FormControl>
									<FormControl isInvalid={errors.state_id}>
										<FormLabel
											color={textColor}
											fontWeight="bold"
											fontSize="xs"
										>
											Estados
										</FormLabel>
										<Controller
											name="state_id"
											{...register('state_id')}
											control={control}
											render={({
												field: { onBlur, value }
											}) => (
												<ReactSelect
													name="plans"
													options={states.map(
														(st) => ({
															value: st.id,
															label: st.name
														})
													)}
													isMulti
													isClearable
													backspaceRemovesValue
													placeholder="Selecione"
													onBlur={onBlur}
													selected={value}
													onChange={(value) =>
														handleStatesChange(
															value
														)
													}
													styles={useColorModeValue(
														msLightTheme,
														msDarkTheme
													)}
												/>
											)}
										/>
										{errors.state_id && (
											<FormErrorMessage>
												{errors.state_id.message}
											</FormErrorMessage>
										)}
									</FormControl>
								</Grid>
								<Flex
									w="full"
									justifyContent="flex-end"
									mt="24px"
								>
									<HStack spacing={4}>
										<Button
											disabled={loading}
											variant="ghost"
											colorScheme="gray"
											alignSelf="flex-end"
											size="md"
											onClick={() => history.goBack()}
										>
											Cancelar
										</Button>
										<Button
											isLoading={loading}
											disabled={loading}
											colorScheme="green"
											type="submit"
											alignSelf="flex-end"
											size="md"
										>
											Cadastrar
										</Button>
									</HStack>
								</Flex>
							</Flex>
						</form>
					</FormProvider>
				</CardBody>
			</Card>
		</Flex>
	);
};

export default CreateUnit;
